// Phase 1.3 — PC view of advisor coverage health.
// docs/founder-advisor-pipe-prd.md §10.
//
// One row per advisor. Columns: name + role, covered companies, hours
// since last advisor touch (any company), open founder-escalations in last
// 7 days, health bucket. Sorted needs_attention → drifting → ok.
//
// This page is admin-only (requireAdmin via /api/admin/advisor-health).
// Theme: matches dark admin shell (admin uses a fixed dark palette).
import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, Clock, Users } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

interface AdvisorHealthRow {
  id: number;
  name: string;
  email: string;
  role: string;
  avatarUrl: string | null;
  coveredCompanies: number;
  openEscalations7d: number;
  lastTouchAt: string | null;
  hoursSinceLastTouch: number | null;
  healthBucket: "needs_attention" | "drifting" | "ok";
}

function authHeaders(): Record<string, string> {
  const t = localStorage.getItem("admin_token") || "";
  return t ? { "x-admin-token": t, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
}

function bucketChip(b: AdvisorHealthRow["healthBucket"]): { label: string; cls: string } {
  switch (b) {
    case "needs_attention": return { label: "Needs attention", cls: "bg-red-500/15 text-red-300 border-red-500/30" };
    case "drifting": return { label: "Drifting", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" };
    default: return { label: "OK", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
  }
}

function fmtHours(h: number | null): string {
  if (h == null) return "Never";
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function AdminAdvisorHealth() {
  const [rows, setRows] = useState<AdvisorHealthRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${BASE}api/admin/advisor-health`, { headers: authHeaders() });
      if (!r.ok) { setError(`HTTP ${r.status}`); setLoading(false); return; }
      const j = await r.json();
      setRows(j.advisors ?? []);
    } catch (e: any) {
      setError(e?.message || "Network error");
    }
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 text-white">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Advisor health</h1>
        <p className="text-sm text-white/60 mt-1">
          Live coverage across the portfolio. PRD §10 — surfaces drift before founders feel it.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-white/60 p-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading advisor health…
        </div>
      )}
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg p-3 text-sm">{error}</div>}

      {!loading && rows && rows.length === 0 && (
        <div className="text-sm text-white/60 p-8">No active advisors.</div>
      )}

      {!loading && rows && rows.length > 0 && (
        <div className="rounded-2xl border border-white/[0.08] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left px-4 py-2.5 font-mono">Advisor</th>
                <th className="text-right px-3 py-2.5 font-mono">Companies</th>
                <th className="text-right px-3 py-2.5 font-mono">Last touch</th>
                <th className="text-right px-3 py-2.5 font-mono">Open esc (7d)</th>
                <th className="text-right px-4 py-2.5 font-mono">Health</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const chip = bucketChip(r.healthBucket);
                const initials = r.name.split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();
                return (
                  <tr key={r.id} className="border-t border-white/[0.06]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {r.avatarUrl
                          ? <img src={r.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover border border-white/10" />
                          : <div className="w-8 h-8 rounded-full bg-white/[0.08] border border-white/10 flex items-center justify-center text-xs font-semibold">{initials}</div>}
                        <div>
                          <div className="font-medium">{r.name}</div>
                          <div className="text-[11px] text-white/45">{r.role} · {r.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      <span className="inline-flex items-center gap-1 text-white/80"><Users className="w-3 h-3" /> {r.coveredCompanies}</span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      <span className={`inline-flex items-center gap-1 ${r.hoursSinceLastTouch != null && r.hoursSinceLastTouch > 7 * 24 ? "text-amber-300" : "text-white/80"}`}>
                        <Clock className="w-3 h-3" /> {fmtHours(r.hoursSinceLastTouch)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {r.openEscalations7d > 0
                        ? <span className="inline-flex items-center gap-1 text-red-300"><AlertTriangle className="w-3 h-3" /> {r.openEscalations7d}</span>
                        : <span className="text-white/40">0</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${chip.cls}`}>{chip.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
